import { useCallback, useMemo } from 'react';
import { ListItemButton } from '@mui/material';
import { gridRowSelectionIdsSelector, useGridApiContext, useGridSelector } from '@mui/x-data-grid-pro';
import { clearLegFeedback, setLegLoading } from '@/Board/dispatchBoardSlice';
import { appendErrorAnnouncement } from '@/Infrastructure/Components/Announcements/AnnouncementSlice';
import { getApiErrorMessage } from '@api/getApiError';
import { useDeleteLegMutation } from '@api/Trinium/Endpoints/Leg';
import { resolveDeleteDriverPayments } from '@api/Trinium/Endpoints/Leg/actions';
import { DispatchLeg } from '@api/Trinium/Endpoints/Dispatch/types';
import { LegStatus } from '@api/Trinium/types';
import DisabledTooltip from '@components/DisabledTooltip';
import { confirmAsync } from '@components/Modals/ConfirmPopup';
import useCurrentTerminal from '@hooks/useCurrentTerminal';
import { useAppDispatch } from '@reduxStore';

/**
 * Remove Leg Action
 */
export default function RemoveLegAction() {
	const appDispatch = useAppDispatch();
	const gridApiRef = useGridApiContext();
	const { number: terminalNumber } = useCurrentTerminal();
	const [deleteLeg, { isLoading: removeLegPending }] = useDeleteLegMutation();
	const selectedRowIds = useGridSelector(gridApiRef, gridRowSelectionIdsSelector);

	const leg = useMemo(() => {
		const legId = selectedRowIds.keys().next().value;
		return legId ? gridApiRef.current.getRow<DispatchLeg>(legId) : undefined;
	}, [gridApiRef, selectedRowIds]);

	/**
	 * Validation on the Remove Leg action item. Users cannot remove the selected leg from the consignment
	 * if it is the only leg on the consignment or the leg has a status of picked up or delivered.
	 */
	const removeLegValidation = useMemo(() => {
		if (!leg) return undefined;

		const { csnLegCount, legStatus } = leg;

		if (removeLegPending)
			return {
				disabled: true,
				reason: 'Remove Leg Request Pending'
			};

		if (csnLegCount === 1)
			return {
				disabled: true,
				reason: 'Cannot remove the only leg on the Consignment.'
			};

		const isDisabled = LegStatus[legStatus] === LegStatus.DL || LegStatus[legStatus] === LegStatus.PU;

		return {
			disabled: isDisabled,
			reason: isDisabled ? 'Cannot remove legs with status of "Picked Up" or "Delivered".' : undefined
		};
	}, [leg, removeLegPending]);

	const handleBtnClick = useCallback(async () => {
		if (!leg) return;

		const { csnId, legId, legNo, jobId } = leg;

		if (!(await confirmAsync(`Are you sure you want to remove Leg ${String(legNo)} from the Consignment?`))) return;

		const deleteDriverPayments = await resolveDeleteDriverPayments(terminalNumber, legId);
		appDispatch(setLegLoading(legId));
		await deleteLeg({
			terminalNumber: terminalNumber,
			id: legId,
			csnId: csnId,
			jobId: jobId,
			deleteDriverPayments
		})
			.unwrap()
			.catch((ex: unknown) => {
				appDispatch(
					appendErrorAnnouncement({
						message: getApiErrorMessage(ex)
					})
				);
			})
			.finally(() => {
				appDispatch(clearLegFeedback(legId));
			});
	}, [appDispatch, leg, terminalNumber, deleteLeg]);

	// This action requires exactly 1 row to be selected and have Leg value
	if (selectedRowIds.size !== 1 || !leg || !removeLegValidation) {
		return <></>;
	}

	return (
		<DisabledTooltip message={removeLegValidation.reason}>
			<ListItemButton
				onClick={() => void handleBtnClick()}
				disabled={removeLegValidation.disabled}
			>
				Remove Leg
			</ListItemButton>
		</DisabledTooltip>
	);
}
